import * as React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import toast from 'react-hot-toast';
import {
  AI_SEARCH_ENTITY_LABELS,
  AI_SEARCH_ENTITY_OPTIONS,
  type AiSearchEntityType,
  type AiSearchResultItem,
  fetchAiSearch,
} from '../../services/aiSearchService';
import { aiFonteFromItem } from '../../utils/aiFonteDisplay';
import { AiSearchPayloadSummary } from './AiSearchPayloadSummary';
import { AiDiagnosisContextInfo } from './AiDiagnosisContextInfo';

export type AiSearchModuleVariant = 'page' | 'modal';

export type AiSearchModuleProps = {
  variant: AiSearchModuleVariant;
  /** In modalità contestuale il filtro tipo dato è fisso (nessun controllo tipo in UI). */
  fixedEntityType?: AiSearchEntityType;
  /** Diagnosi corrente (opzionale, per arricchimento futuro di `/ai/search`). */
  diagnosisId?: string;
  /** Inserimento nella diagnosi (modalità modal). */
  onInsert?: (item: AiSearchResultItem) => void | Promise<void>;
};

function primaryLabel(item: AiSearchResultItem): string {
  const p = item.payload;
  const pick = p.code ?? p.formula ?? p.title ?? p.description;
  if (pick != null && String(pick).trim() !== '') return String(pick);
  return item.uid;
}

export const AiSearchModule: React.FC<AiSearchModuleProps> = ({
  variant,
  fixedEntityType,
  diagnosisId,
  onInsert,
}) => {
  const [selectedType, setSelectedType] = React.useState<AiSearchEntityType>(
    AI_SEARCH_ENTITY_OPTIONS[0]?.value ?? 'intervention'
  );
  const [query, setQuery] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [results, setResults] = React.useState<AiSearchResultItem[]>([]);
  /** Query dell’ultima ricerca inviata (pulsante o Invio). */
  const [submittedQuery, setSubmittedQuery] = React.useState<string | null>(null);

  const runSearch = React.useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed) {
      toast.error('Inserisci un testo di ricerca');
      setResults([]);
      setSubmittedQuery(null);
      return;
    }
    const entityType = fixedEntityType ?? selectedType;
    setLoading(true);
    setSubmittedQuery(trimmed);
    try {
      const list = await fetchAiSearch({
        entityTypes: [entityType],
        query: trimmed,
        id_entity: diagnosisId ?? null,
        top_k: variant === 'page' ? 50 : 30,
      });
      setResults(list);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Ricerca AI non riuscita';
      toast.error(msg);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [query, selectedType, fixedEntityType, diagnosisId, variant]);

  const handleInsert = async (item: AiSearchResultItem) => {
    try {
      await onInsert?.(item);
      if (onInsert) {
        // toast.success('Elemento aggiunto alla diagnosi');
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Impossibile inserire l’elemento';
      toast.error(msg);
    }
  };

  const showInsert = variant === 'modal';

  return (
    <Stack spacing={2} sx={{ width: '100%', py: variant === 'page' ? 0 : 0.5 }}>
      {diagnosisId ? <AiDiagnosisContextInfo /> : null}
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          fullWidth
          placeholder="Cerca…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          disabled={loading}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void runSearch();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          size="medium"
          disabled={loading || !query.trim()}
          onClick={() => void runSearch()}
          sx={{ flexShrink: 0, minHeight: 40 }}
        >
          Cerca
        </Button>
      </Stack>

      {!fixedEntityType && (
        <FormControl component="fieldset" disabled={loading}>
          <FormLabel component="legend" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            Tipo di dato
          </FormLabel>
          <RadioGroup
            row
            value={selectedType}
            onChange={e => setSelectedType(e.target.value as AiSearchEntityType)}
            sx={{ flexWrap: 'wrap', gap: { xs: 0, sm: 1 } }}
          >
            {AI_SEARCH_ENTITY_OPTIONS.map(opt => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                control={<Radio size="small" />}
                label={opt.label}
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}

      <Box
        sx={{
          minHeight: 200,
          maxHeight: variant === 'page' ? 'calc(100vh - 320px)' : 'calc(85vh - 220px)',
          overflow: 'auto',
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}
        {!loading && submittedQuery !== null && results.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            Nessun risultato per «{submittedQuery}».
          </Typography>
        )}
        {!loading && submittedQuery === null && (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            Digita nella barra di ricerca e premi Cerca o Invio.
          </Typography>
        )}
        {!loading && results.length > 0 && (
          <Stack spacing={0}>
            {results.map(item => {
              const fonte = aiFonteFromItem(item);
              return (
              <Accordion key={item.uid} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="baseline"
                    flexWrap="wrap"
                    sx={{ width: '100%', pr: 1 }}
                  >
                    <Typography variant="subtitle2" component="span" fontWeight={700}>
                      {primaryLabel(item)}
                    </Typography>
                    <Typography variant="caption" color="primary">
                      {AI_SEARCH_ENTITY_LABELS[item.entityType]}
                    </Typography>
                    {fonte ? (
                      <Typography variant="caption" color="text.secondary">
                        · Fonte: {fonte}
                      </Typography>
                    ) : null}
                    <Typography variant="caption" color="text.secondary">
                      · {item.tipo === 'external' ? 'esterno' : 'interno'}
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <AiSearchPayloadSummary item={item} />
                  {showInsert ? (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon fontSize="small" />}
                        onClick={() => void handleInsert(item)}
                      >
                        Inserisci
                      </Button>
                    </Box>
                  ) : null}
                </AccordionDetails>
              </Accordion>
            );
            })}
          </Stack>
        )}
      </Box>
    </Stack>
  );
};
